// API client for the inline product customiser.
// All worker endpoints are absolute (worker URL) because the bundle runs
// inside the storefront page, not on the worker's own domain.
// Storefront endpoints (/cart/add.js, /cart.js) are same-origin and use
// relative URLs.

import type { Config } from "./types";

export async function fetchConfig(workerUrl: string, productGid: string): Promise<Config> {
  // The embed launcher prefetches config in parallel with the bundle
  // download (window.__RK_PC_CONFIG__). Consume that in-flight result
  // when present so the customiser doesn't wait on a fresh round-trip
  // after the bundle mounts.
  const pre = (
    window as unknown as {
      __RK_PC_CONFIG__?: Record<string, Promise<Config>>;
    }
  ).__RK_PC_CONFIG__?.[productGid];
  if (pre) {
    try {
      return await pre;
    } catch {
      // Prefetch failed — fall through to a fresh fetch below.
    }
  }
  const qs = new URLSearchParams({ productGid });
  const resp = await fetch(`${workerUrl.replace(/\/$/, "")}/api/config?${qs.toString()}`);
  if (!resp.ok) {
    const t = await resp.text();
    throw new Error(`config_failed: ${resp.status} ${t.slice(0, 200)}`);
  }
  return (await resp.json()) as Config;
}

export async function uploadStaging(
  workerUrl: string,
  file: File,
  customerEmail?: string,
): Promise<{
  stagingKey: string;
  contentType: string;
  size: number;
  /** sha256 of the raw file bytes. Used by Phase 19 cross-order
   *  digitisation memory to skip the fee on repeat uploads. */
  fileHash?: string;
  /** True when this (email, hash) pair was already paid for in a past
   *  order. Customiser pricing.ts skips the $30 fee when set. */
  alreadyDigitised?: boolean;
  /** Name of the earlier order that paid for digitisation, for the
   *  "Already digitised, no extra fee" hint copy. */
  firstOrderName?: string | null;
}> {
  const form = new FormData();
  form.append("file", file);
  if (customerEmail) form.append("customerEmail", customerEmail);
  const resp = await fetch(`${workerUrl.replace(/\/$/, "")}/api/upload`, {
    method: "POST",
    body: form,
  });
  if (!resp.ok) {
    const t = await resp.text();
    throw new Error(`upload_failed: ${resp.status} ${t.slice(0, 200)}`);
  }
  return (await resp.json()) as {
    stagingKey: string;
    contentType: string;
    size: number;
    fileHash?: string;
    alreadyDigitised?: boolean;
    firstOrderName?: string | null;
  };
}

// Build the URL for an uploaded file so <img src=...> can render it
// during the design flow (preview-side, not the embroidery render).
export function uploadedFileUrl(workerUrl: string, stagingKey: string): string {
  const qs = new URLSearchParams({ key: stagingKey });
  return `${workerUrl.replace(/\/$/, "")}/api/file?${qs.toString()}`;
}

// Phase 31: ingest a verified past-order proof PDF as the customer's
// new logo. Worker re-verifies the URL belongs to the order (so a
// stolen URL won't unlock the fee waiver), downloads the bytes, stages
// to R2 + returns a stagingKey shaped identically to /api/upload. The
// `alreadyDigitised: true` flag means pricing.ts won't charge the $30
// setup fee — same gate as Phase 19 cross-order memory.
export async function ingestProof(
  workerUrl: string,
  orderName: string,
  email: string,
  proofUrl: string,
): Promise<{
  ok: boolean;
  stagingKey?: string;
  contentType?: string;
  size?: number;
  fileHash?: string;
  filename?: string;
  alreadyDigitised?: boolean;
  sourceOrderName?: string;
  error?: string;
}> {
  try {
    const resp = await fetch(
      `${workerUrl.replace(/\/$/, "")}/api/ingest-proof`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ orderName, email, proofUrl }),
      },
    );
    if (!resp.ok) {
      const t = await resp.text();
      return { ok: false, error: t.slice(0, 200) };
    }
    const j = (await resp.json()) as {
      stagingKey: string;
      contentType: string;
      size: number;
      fileHash: string;
      filename: string;
      alreadyDigitised: boolean;
      sourceOrderName: string;
    };
    return { ok: true, ...j };
  } catch (e) {
    return {
      ok: false,
      error: (e as Error).message?.slice(0, 200) ?? "network",
    };
  }
}

// Phase 28: look up spots from a customer's past order by name + email.
// Returns the same shape the cart-side reuse path produces so a single
// onPick callback in App.tsx can consume both. The worker route enforces
// the email gate + a per-IP rate limit; a wrong pair gets a 404 here.
//
// The draft type matches EmbroideryDraft from sections/PlacementSection so
// DesignCard + the editor can consume past-order spots and cart spots
// interchangeably. The worker decodes raw line-item properties into this
// shape so the client never has to.
import type { EmbroideryDraft } from "./sections/PlacementSection";

export type PastOrderSpot = {
  key: string;
  sourceLabel: string;
  draft: EmbroideryDraft;
};

// Phase 30: design-proof PDF surfaced from the order's timeline comments.
// URL is short-lived (Shopify's signed Google Storage URL ~2.6h TTL); we
// don't cache. UI renders one downloadable pill per proof above the
// spot-cards row.
export type PastOrderProof = {
  name: string;
  url: string;
  createdAt: string;
};

export type PastOrderResult = {
  ok: boolean;
  spots: PastOrderSpot[];
  proofs: PastOrderProof[];
  error?: string;
};

export async function fetchPastOrderSpots(
  workerUrl: string,
  orderName: string,
  email: string,
): Promise<PastOrderResult> {
  try {
    const resp = await fetch(
      `${workerUrl.replace(/\/$/, "")}/api/order-spots`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ orderName, email }),
      },
    );
    if (resp.status === 404) {
      return { ok: false, spots: [], proofs: [], error: "not_found" };
    }
    if (resp.status === 429) {
      return { ok: false, spots: [], proofs: [], error: "rate_limited" };
    }
    if (!resp.ok) {
      const t = await resp.text();
      return { ok: false, spots: [], proofs: [], error: t.slice(0, 200) };
    }
    const j = (await resp.json()) as {
      spots: PastOrderSpot[];
      proofs?: PastOrderProof[];
    };
    return { ok: true, spots: j.spots ?? [], proofs: j.proofs ?? [] };
  } catch (e) {
    return {
      ok: false,
      spots: [],
      proofs: [],
      error: (e as Error).message?.slice(0, 200) ?? "network",
    };
  }
}

// Fetch the customer's current cart (same-origin Shopify endpoint).
// Returns the raw JSON; the caller is responsible for filtering for
// rk-builder lines + decoding the per-spot properties.
export async function fetchCart(): Promise<CartJson | null> {
  try {
    const resp = await fetch("/cart.js", { credentials: "same-origin", headers: { accept: "application/json" } });
    if (!resp.ok) return null;
    return (await resp.json()) as CartJson;
  } catch {
    return null;
  }
}

export type CartJson = {
  items: Array<{
    id: number;
    variant_id: number;
    quantity: number;
    title: string;
    variant_title?: string | null;
    image?: string | null;
    /** Product handle - present on Shopify's /cart.js payload, used by
     *  Phase 80's pet multi-buy discount preview to count "how many pet
     *  items are already in the cart" so the bundle tier carries
     *  across multiple add-to-cart sessions. */
    handle?: string | null;
    properties?: Record<string, string> | null;
    /** Per-unit price in cents, before line-level discounts. Used to rank
     *  existing custom units by price in the multi-buy preview. Raw
     *  /cart.js lines are pre-transform, so garment lines are
     *  garment-only priced. */
    price?: number;
    /** Line total in cents (price x quantity, after line-level discounts).
     *  Real on Shopify's /cart.js payload; the threshold maths subtracts
     *  it for service-fee lines (Father's Day rush) so a delivery upgrade
     *  never counts as garment spend. */
    line_price?: number;
  }>;
  /** Sum of line-level discounted prices in cents (Shopify /cart.js).
   *  Used by the foot's threshold unlock line to project the cart total
   *  after this item is added. */
  items_subtotal_price?: number;
};

// Same-origin Shopify cart add. Properties keys starting with `_` are
// hidden from the customer in the cart UI but kept on the line item.
export async function addToCart(
  variantId: string,
  quantity: number,
  properties: Record<string, string>,
): Promise<unknown> {
  const resp = await fetch("/cart/add.js", {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ id: Number(variantId), quantity, properties }),
  });
  const text = await resp.text();
  if (!resp.ok) {
    try {
      const j = JSON.parse(text);
      throw new Error(j.description || j.message || `cart_add_failed_${resp.status}`);
    } catch {
      throw new Error(`cart_add_failed_${resp.status}`);
    }
  }
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

// Phase B6: fetch the selected garment variant's price (in cents) from
// the storefront product JSON, so the customiser foot can show a true
// "Item total" (garment + embroidery), not just the embroidery upcharge.
// Same-origin; returns null on any failure so the caller falls back to
// the embroidery-only total.
export async function fetchVariantPriceCents(
  productHandle: string,
  variantId: string,
): Promise<number | null> {
  try {
    const resp = await fetch(`/products/${productHandle}.js`, {
      credentials: "same-origin",
      headers: { accept: "application/json" },
    });
    if (!resp.ok) return null;
    const j = (await resp.json()) as {
      variants?: Array<{ id: number; price: number }>;
    };
    const v = j.variants?.find((x) => String(x.id) === String(variantId));
    return typeof v?.price === "number" ? v.price : null;
  } catch {
    return null;
  }
}

// Phase 73: fetch the full /products/<handle>.js shape (options +
// variants). Used by the variant-binding resolver to map a custom-
// field selection to a Shopify variant id. Same-origin; null on
// failure so callers can no-op.
export type ProductJsonShape = {
  options?: Array<string | { name: string }>;
  /** Real product title from /products/<handle>.js - preferred over the
   *  resolver's slug-derived member titles in the bundle rows. */
  title?: string;
  /** Protocol-relative CDN URL, e.g. "//rkthreads.com/cdn/shop/...". */
  featured_image?: string | null;
  variants?: Array<{
    id: number | string;
    option1?: string | null;
    option2?: string | null;
    option3?: string | null;
    price?: number;
    /** From /products/<handle>.js - false when the size/colour is sold
     *  out. Drives the sold-out pre-order branch. */
    available?: boolean;
    title?: string | null;
    sku?: string | null;
  }>;
};
export async function fetchProductJson(
  productHandle: string,
): Promise<ProductJsonShape | null> {
  try {
    const resp = await fetch(`/products/${productHandle}.js`, {
      credentials: "same-origin",
      headers: { accept: "application/json" },
    });
    if (!resp.ok) return null;
    return (await resp.json()) as ProductJsonShape;
  } catch {
    return null;
  }
}

// Same-origin Shopify cart add for MULTIPLE line items in one atomic
// request. Shopify's /cart/add.js accepts an `items` array; either every
// item is added or none are. The customiser uses this so a customised
// garment line can never land in the cart without its paired
// "Embroidery customisation" surcharge line (and vice-versa).
export async function addItemsToCart(
  items: Array<{
    id: number;
    quantity: number;
    properties: Record<string, string>;
  }>,
): Promise<unknown> {
  const resp = await fetch("/cart/add.js", {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ items }),
  });
  const text = await resp.text();
  if (!resp.ok) {
    let msg = `cart_add_failed_${resp.status}`;
    try {
      const j = JSON.parse(text) as { description?: string; message?: string };
      msg = j.description || j.message || msg;
    } catch {
      // Body wasn't JSON — keep the generic status message.
    }
    throw new Error(msg);
  }
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

// Persist the full builder design property bag server-side before adding
// the garment to the cart. On success the caller emits only the human-
// readable line-item properties + the `_rk_group_id` token; the machine-
// readable bag lives in the admin DB keyed by groupId, so it never
// clutters the admin order page.
//
// Returns false on ANY failure (network, rate limit, admin error) so the
// caller can fall back to putting the full property bag on the cart line
// — functional, just cluttered. The order is never put at risk by a
// save-design hiccup.
// `extras.email` + `extras.productHandle` are optional enrichment: when a
// known email rides along, the admin marks the matching BuilderDraft
// converted (draft->order attribution) and stamps the Klaviyo profile's
// rk_last_design_* properties. The cart-line property bag itself is
// untouched - the orders/create webhook sees exactly what it always saw.
//
// Audit 2026-10: this call is awaited BEFORE /cart/add.js, so a stalled
// admin (cold start, upstream hang) used to leave the button on "Adding..."
// for as long as the browser's own fetch timeout. The abort signal caps
// the wait; the abort lands in the catch below and the caller takes the
// full-property-bag fallback within seconds. Guarded so a browser without
// AbortSignal.timeout (pre-2022 Safari) just runs the old untimed fetch.
const SAVE_DESIGN_TIMEOUT_MS = 8000;
export async function saveDesign(
  workerUrl: string,
  groupId: string,
  productGid: string,
  properties: Record<string, string>,
  extras?: { email?: string | null; productHandle?: string | null },
): Promise<boolean> {
  try {
    const signal =
      typeof AbortSignal !== "undefined" &&
      typeof AbortSignal.timeout === "function"
        ? AbortSignal.timeout(SAVE_DESIGN_TIMEOUT_MS)
        : undefined;
    const resp = await fetch(`${workerUrl.replace(/\/$/, "")}/api/save-design`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        groupId,
        productGid,
        properties,
        email: extras?.email ?? null,
        productHandle: extras?.productHandle ?? null,
      }),
      signal,
    });
    return resp.ok;
  } catch {
    return false;
  }
}

// Mirror the customiser's persisted state to the admin's BuilderDraft
// table (via the worker's /api/save-draft) so the abandoned-design
// recovery email can restore the design on any device. Only called once
// an email is known. Returns false on ANY failure so the caller can
// re-arm and retry on the next state change - a hiccup never surfaces
// to the customer.
export async function saveDraft(
  workerUrl: string,
  payload: {
    id: string;
    email: string;
    productGid: string;
    productHandle: string;
    templateTag?: string | null;
    state: unknown;
    thumbUrl?: string | null;
    source: "guest_card" | "logged_in";
  },
): Promise<boolean> {
  try {
    const resp = await fetch(`${workerUrl.replace(/\/$/, "")}/api/save-draft`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    });
    return resp.ok;
  } catch {
    return false;
  }
}

// Resolve a ?rk_draft=<id>&e=<email> resume deep link back into the
// saved customiser state. The worker answers 404 for every failure mode
// (unknown id, wrong email, rate limit burn-through), so the caller just
// treats !ok as "nothing to restore".
export async function lookupDraft(
  workerUrl: string,
  draftId: string,
  email: string,
): Promise<{ ok: boolean; state?: unknown; productHandle?: string }> {
  try {
    const resp = await fetch(`${workerUrl.replace(/\/$/, "")}/api/draft-lookup`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ draftId, email }),
    });
    if (!resp.ok) return { ok: false };
    const j = (await resp.json()) as { state?: unknown; productHandle?: string };
    if (!j || typeof j !== "object" || !j.state) return { ok: false };
    return { ok: true, state: j.state, productHandle: j.productHandle };
  } catch {
    return { ok: false };
  }
}

// Fire a "Started Custom Design" signal to the worker (which proxies it to
// the admin's Klaviyo Events API) for abandoned-design recovery. Called the
// moment a real design exists AND we know an email (logged-in or guest-
// captured). Fire-and-forget: never throws, never awaited, never blocks the
// design flow. `keepalive` lets it complete even if the customer navigates
// away immediately after (which is exactly the abandon case we want).
export function trackDesignStarted(
  workerUrl: string,
  payload: {
    email: string;
    productGid: string;
    productHandle: string;
    templateTag?: string | null;
    templateName?: string | null;
    spotCount?: number;
    pageUrl?: string;
    /** Draft token (the future _rk_group_id) so the recovery email can
     *  deep-link back into the saved design via ?rk_draft=<id>. */
    draftId?: string | null;
    /** First spot's artwork /api/file URL for the email's thumb. */
    thumbUrl?: string | null;
  },
): void {
  if (!payload.email) return;
  try {
    void fetch(`${workerUrl.replace(/\/$/, "")}/api/track-design`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // best-effort
  }
}
